import React from 'react';
import PropTypes from 'prop-types';

import { Redirect } from 'react-router';

import { Logger } from 'aws-amplify';

import { 
  default as useLocalStorageState,
}                                 from 'use-local-storage-state';

import { Paper }                  from '@material-ui/core';

import { makeStyles }             from '@material-ui/core/styles';

import { 
  ExecutionDataTable,
  fmtExecutionEndTime,
  fmtExecutionIssues,
  fmtExecutionOverrides,
  fmtExecutionProgress,
  fmtExecutionResult,
  fmtExecutionStatus,
  useExecutions,
}                                 from '..';

import { 
  fmtDaysHrsMinFromSecs,
  fmtIso8601AsLocalDateHrsMin,
}                                 from '../../utils/formatters';

import { 
  getColumnsDisplayed,
  getEffectiveColumnDefs,
}                                 from "../../utils/helpers";

import { 
  fmtClient,
}                                 from '../../../components/client';

import { 
  UserContext,
}                                 from "../../user";

import { 
  ResourceRef,
}                                 from "../../resource";

import { FieldValue }             from "../../../packages/detail-panel";

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
}));

const logger = new Logger('Executions');

export default function Executions(props) {
  
  const { workload, columnDefs, title, noHeader, prefName } = props;  
  const classes = useStyles();
  const [selected, setSelected] = React.useState(null);
  const basePrefId = `exstat_${prefName}`;
  const [columnOrder, setColumnOrder] = useLocalStorageState(basePrefId+"_co", null);
  const [columnsDisplayed, setColumnsDisplayed] = useLocalStorageState(basePrefId+"_cd", null);
  const userContext=React.useContext(UserContext);

  const { executions, loading, error } = useExecutions({
    clientAccountId: userContext.selectedClient,
    workloadId: workload, 
    subscribed: 'all'
  });

  if (error) {
    logger.error(error);
    return(<div>Error: see console</div>);
  }

  const handlePrefsChange = (event) => {
    if(event.type==='COLUMN_ORDER') {
      setColumnOrder(event.newColumnOrder);
    } else if (event.type==='VIEW_COLUMNS') {
      let currentColumnsDisplayed=getColumnsDisplayed(baseColumnDefs,columnDefs,columnsDisplayed);
      if(event.action==='add') {
        currentColumnsDisplayed.push(event.changedColumn);
        setColumnsDisplayed(currentColumnsDisplayed);
      } else {
        setColumnsDisplayed(currentColumnsDisplayed.filter(e => e !== event.changedColumn));
      }
    }
  };

  const clientAccountAliases = new Set();
  executions.forEach((execution) => {
    clientAccountAliases.add(fmtClient(execution.ClientAccountId, userContext ));
  });
  
  const baseColumnDefs = [
    { name: 'ClientAccountId', label: 'Owner', clickable: true, options: { 
      customBodyRenderLite: (dataIndex) => <ResourceRef value={executions[dataIndex].ClientAccountId} type='Client'/>, 
      // customBodyRenderLite: (dataIndex) => fmtClient(executions[dataIndex].ClientAccountId, userContext ), 
      customFilterListOptions: { render: v => `Owner: ${v}` },
      filterType: 'multiselect',
      filterOptions: {
        names: Array.from(clientAccountAliases),
        logic: (location, filters, row) => {
          if (filters.length) return !filters.includes(fmtClient(location, userContext ));
          return false;
        },
      },
     }
    },
    { name: 'WorkloadId', label: 'Workload', clickable: true, options: { 
      filter:false, 
      customBodyRenderLite: (dataIndex) => <ResourceRef value={executions[dataIndex].WorkloadId} type='Workload'/>, 
     }
    },
    { name: 'ExecutionId', label: 'Execution', clickable: true, options: { 
      filter: false,
      customBodyRenderLite: (dataIndex) => <FieldValue 
        value={executions[dataIndex].ExecutionId} 
        maxLength={6}
        onClick={handleSelectById}/> 
    } },
    { name: 'Description', options: { display: false, filter:false } },
    { name: 'Overrides', options: { display: false, filter: false, customBodyRenderLite: (dataIndex) => fmtExecutionOverrides(executions[dataIndex]) } },
    { name: 'StartTime', label: 'Start Time', options: { filter:false, customBodyRender: fmtIso8601AsLocalDateHrsMin } },
    { name: 'EndTime', label: 'End Time', options: { filter:false, customBodyRender: fmtExecutionEndTime } },
    { name: 'Tasks.RuntimeSecs', label: 'CPU Time', options: { filter:false, customBodyRender: fmtDaysHrsMinFromSecs } },
    { name: 'Feed' },
    { name: 'Issues', clickable: true, options: { filter:false, customBodyRenderLite: (dataIndex) => fmtExecutionIssues(executions[dataIndex]) } },
    { name: 'Progress', options: { filter: false, customBodyRenderLite: (dataIndex) => fmtExecutionProgress(executions[dataIndex].Inputs) } },
    { name: 'Status', options: { customBodyRenderLite: (dataIndex) => fmtExecutionStatus(executions[dataIndex]) } },
    { name: 'Result', options: { customBodyRender: fmtExecutionResult } },
  ];

  if(!executions) {
    return(null);
  } 

  // logger.info("EXECUTIONS DATA: ",executions);
  
  const handleSelectById = (executionId) => {
    setSelected(executions.find( e => e.ExecutionId===executionId));
  };

  const handleSelect = (execution) => {
    setSelected(execution);
  };

  if(selected) {
    return <Redirect push to={"/executions/"+selected.ExecutionId} />;  
  }

  // during console development its not unusual for new columnDefs to be added/removed for a resource type
  // its important that the columnOrder array length matches the effective column defs array length
  // since stored column order preference may be stale we ignore it when definitions have changed
  // forcing user to rerrange columns from among the updated definitions again 
  const effectiveColumnDefs=getEffectiveColumnDefs(baseColumnDefs,columnDefs,columnsDisplayed);
  const effectiveColumnOrder=(columnOrder&&columnOrder.length===effectiveColumnDefs.length)?columnOrder:null;

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <ExecutionDataTable
          data={executions}
          columns={effectiveColumnDefs}
          columnOrder={effectiveColumnOrder}
          loading={loading}
          title={title}
          noHeader={noHeader}
          onSelect={handleSelect}
          onChangePrefs={handlePrefsChange}
        /> 
      </Paper>
    </div>
  );
  
}

Executions.propTypes = {
  workload: PropTypes.string,
  columnDefs: PropTypes.array.isRequired,
  title: PropTypes.string,
  noHeader: PropTypes.bool,
  prefName: PropTypes.string.isRequired,
};

Executions.defaultProps = {
  columnDefs: [],
  prefName: 'default',
};
